import {useContext, useEffect, useMemo, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {BottomSheet} from "@/components";
import {trpc} from "@/app/trpc";
import {showErrorToast} from "@/utils/toast";
import {getMaxBookingDate, getMinBookingDate} from "@chayka/utils";
import {
  type BookingWizardSlide,
  BOOKING_WIZARD_SLIDES,
  BOOKING_WIZARD_SLIDES_ORDER,
  BOOKING_WIZARD_SLIDES_TITLES,
  PROGRESS_CLASSES_BY_SLIDE,
} from "./constants";
import {Button, ShadowBox, XStack, YStack, ProgressBar} from "@chayka/ui";
import {
  type BookingWizardSlideComponent,
  SuperTypeSlide,
  SubTypeSlide,
  RoomTimeSlide,
  ParkingSlide,
  EquipmentSlide,
  ConfirmSlide,
  SuccessSlide,
} from "./wizard";
import {BookingStateContext} from "./state";
import {useAuth} from "@/hooks/useAuth";

const DATE_QUERY_PARAM_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ROOM_ID_QUERY_PARAM_PATTERN = /^\d+$/;

const getPreselectedBookingDate = (value: string | null) => {
  if (!value) {
    return null;
  }

  const match = DATE_QUERY_PARAM_PATTERN.exec(value);

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  if (
    date.getTime() < getMinBookingDate().getTime() ||
    date.getTime() > getMaxBookingDate().getTime()
  ) {
    return null;
  }

  return date;
};

const getPreselectedRoomId = (value: string | null) => {
  if (!value || !ROOM_ID_QUERY_PARAM_PATTERN.test(value)) {
    return null;
  }

  const roomId = Number(value);

  return Number.isSafeInteger(roomId) && roomId > 0 ? roomId : null;
};

const ComponentBySlide: Record<BookingWizardSlide, BookingWizardSlideComponent> = {
  [BOOKING_WIZARD_SLIDES.SUPER_TYPE]: SuperTypeSlide,
  [BOOKING_WIZARD_SLIDES.TYPE]: SubTypeSlide,
  [BOOKING_WIZARD_SLIDES.ROOM_TIME]: RoomTimeSlide,
  [BOOKING_WIZARD_SLIDES.PARKING]: ParkingSlide,
  [BOOKING_WIZARD_SLIDES.EQUIPMENT]: EquipmentSlide,
  [BOOKING_WIZARD_SLIDES.CONFIRM]: ConfirmSlide,
  [BOOKING_WIZARD_SLIDES.SUCCESS]: SuccessSlide,
};

export const BookingPageAuthorized = () => {
  const {user} = useAuth();

  if (!user) {
    throw new Error("Нужно авторизоваться");
  }

  const bookingState = useContext(BookingStateContext);
  const [slide, setSlide] = useState<BookingWizardSlide>(BOOKING_WIZARD_SLIDES.SUPER_TYPE);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [nextText, setNextText] = useState("Далее");
  const [preselectedDate] = useState(() => {
    return getPreselectedBookingDate(new URLSearchParams(window.location.search).get("date"));
  });
  const [preselectedRoomId] = useState(() => {
    return getPreselectedRoomId(new URLSearchParams(window.location.search).get("roomId"));
  });

  const {data: subTypes} = useQuery({
    ...trpc.booking.subTypes.queryOptions({
      superTypeCode: bookingState.superType?.code || "",
      roomId: preselectedRoomId || undefined,
    }),
    enabled: Boolean(bookingState.superType),
  });

  const enabledSubTypes = useMemo(() => {
    return (subTypes || []).filter((subType) => !subType.disabled);
  }, [subTypes]);

  const shouldSkipSubTypeSlide = Boolean(
    bookingState.superType &&
      enabledSubTypes.length === 1 &&
      enabledSubTypes[0]?.code === bookingState.superType.code,
  );

  const isParkingWizard = bookingState.subType?.code === "parking";

  const slidesOrder = useMemo(() => {
    return BOOKING_WIZARD_SLIDES_ORDER.filter((item) => {
      if (shouldSkipSubTypeSlide && item === BOOKING_WIZARD_SLIDES.TYPE) {
        return false;
      }

      if (
        isParkingWizard &&
        (
          [BOOKING_WIZARD_SLIDES.ROOM_TIME, BOOKING_WIZARD_SLIDES.EQUIPMENT] as BookingWizardSlide[]
        ).includes(item)
      ) {
        return false;
      }

      return true;
    });
  }, [isParkingWizard, shouldSkipSubTypeSlide]);

  const isResolvingNextSlide = Boolean(bookingState.superType && subTypes === undefined);

  useEffect(() => {
    if (
      shouldSkipSubTypeSlide &&
      enabledSubTypes[0] &&
      bookingState.subType?.code !== enabledSubTypes[0].code
    ) {
      bookingState.setSubType(enabledSubTypes[0]);
    }
  }, [bookingState, enabledSubTypes, shouldSkipSubTypeSlide]);

  useEffect(() => {
    if (slide === BOOKING_WIZARD_SLIDES.TYPE && shouldSkipSubTypeSlide) {
      setIsNextDisabled(false);
      setNextText("Далее");
      setSlide(isParkingWizard ? BOOKING_WIZARD_SLIDES.PARKING : BOOKING_WIZARD_SLIDES.ROOM_TIME);
    }
  }, [isParkingWizard, shouldSkipSubTypeSlide, slide]);

  useEffect(() => {
    if (slide === BOOKING_WIZARD_SLIDES.ROOM_TIME && isParkingWizard) {
      setIsNextDisabled(false);
      setNextText("Далее");
      setSlide(BOOKING_WIZARD_SLIDES.PARKING);
    }
  }, [isParkingWizard, slide]);

  useEffect(() => {
    if (preselectedDate) {
      bookingState.setDate(preselectedDate);
    }
  }, [bookingState, preselectedDate]);

  useEffect(() => {
    bookingState.setQueryRoomId(preselectedRoomId);
  }, [bookingState, preselectedRoomId]);

  useEffect(() => {
    return () => {
      bookingState.reset();
    };
  }, [bookingState]);

  const handleSlideChange = (nextSlide: BookingWizardSlide) => {
    setIsNextDisabled(false);
    setNextText("Далее");
    setSlide(nextSlide);
  };

  const createBookingMutator = useMutation({
    ...trpc.booking.createBooking.mutationOptions(),
    onSuccess: () => {
      handleSlideChange(BOOKING_WIZARD_SLIDES.SUCCESS);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const slideIndex = slidesOrder.indexOf(slide);
  const previousSlide = slideIndex > 0 ? slidesOrder[slideIndex - 1] : null;

  const nextSlide = useMemo(() => {
    return slideIndex < slidesOrder.length - 1 ? slidesOrder[slideIndex + 1] : null;
  }, [slideIndex, slidesOrder]);

  const isConfirmSlide = slide === BOOKING_WIZARD_SLIDES.CONFIRM;

  const handleBottomSheetClose = () => {
    if (slide !== BOOKING_WIZARD_SLIDES.SUCCESS && bookingState.hasDraft) {
      const shouldDiscardBooking = window.confirm(
        "Закрыть бронирование? Все выбранные данные будут сброшены.",
      );

      if (!shouldDiscardBooking) {
        return false;
      }
    }

    return true;
  };

  const handleCreateBooking = (callback: () => void) => {
    const bookingRoom = bookingState.bookingRoom;
    const bookingHours = bookingState.bookingHours;
    const isParkingBooking = bookingState.isParkingBooking;

    if (!bookingState.subType) {
      showErrorToast("Не выбран тип бронирования");
      return;
    }

    if (!bookingRoom) {
      showErrorToast("Не выбрана комната");
      return;
    }

    if (!bookingHours.length) {
      showErrorToast("Не выбрано время бронирования");
      return;
    }

    createBookingMutator.mutate(
      {
        date: bookingState.date,
        roomId: bookingRoom.id,
        roomPrice: bookingRoom.price,
        equipment: bookingState.equipment.map((item) => ({id: item.id, price: item.price})),
        typeCode: bookingState.subType.code,
        hours: bookingHours,
        comment: bookingState.comment.trim(),
        parkingSpotId: isParkingBooking ? undefined : bookingState.parkingSpot?.id,
        parkingHours:
          !isParkingBooking && bookingState.parkingSpot ? bookingState.parkingHours : undefined,
        parkingBrand: bookingState.parkingSpot
          ? bookingState.carModel.trim() || undefined
          : undefined,
        parkingNumber: bookingState.parkingSpot
          ? bookingState.carNumber.trim() || undefined
          : undefined,
      },
      {
        onSuccess: (data) => {
          bookingState.setHasToBeConfirmed(data.hasToBeConfirmed);
          callback();
        },
      },
    );
  };

  const handleNextClick = () => {
    if (!nextSlide) {
      return;
    }

    if (isConfirmSlide) {
      handleCreateBooking(() => handleSlideChange(nextSlide));
      return;
    }

    handleSlideChange(nextSlide);
  };

  const SlideComponent = ComponentBySlide[slide];

  if (user.fines > 0) {
    return (
      <BottomSheet fullHeight isOpen={true} onBeforeClose={handleBottomSheetClose}>
        <YStack className="h-full justify-center items-center gap-10">
          <div className="text-center">
            У вас есть неоплаченные штрафы. Пожалуйста, оплатите их на студии или запросите ссылку
            на оплату и оплатите дистанционно, чтобы продолжить пользоваться нашими услугами.
          </div>
          <Button className="self-stretch" onClick={() => window.location.reload()}>
            Обновить
          </Button>
        </YStack>
      </BottomSheet>
    );
  }

  if (user.confirmationStatus !== "approved") {
    return (
      <BottomSheet fullHeight isOpen={true} onBeforeClose={handleBottomSheetClose}>
        <YStack className="h-full justify-center items-center gap-10">
          <div className="text-center">
            Спасибо за регистрацию <br /> в приложении студии Чайка!
            <br />
            <br />
            Вы сможете создавать бронирования после того, как администратор подтвердит вашу учетную
            запись.
            <br />
            <br />
            Пожалуйста, ожидайте звонка нашего сотрудника с номера <b>+7 (991) 945-75-75</b>
          </div>
          <Button className="self-stretch" onClick={() => window.location.reload()}>
            Обновить
          </Button>
        </YStack>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet fullHeight isOpen={true} onBeforeClose={handleBottomSheetClose}>
      <YStack className="h-full min-h-0 w-full gap-6">
        {slide !== BOOKING_WIZARD_SLIDES.SUCCESS && (
          <YStack className="gap-3 justify-center items-center sticky top-0 z-1 pb-3 -mb-3 bg-background">
            <h1 className="text-center text-xl font-bold">Бронирование</h1>
            <ProgressBar progressClass={PROGRESS_CLASSES_BY_SLIDE[slide]} />
          </YStack>
        )}

        <YStack className="min-h-0 flex-1 gap-6">
          {slide !== BOOKING_WIZARD_SLIDES.SUCCESS && (
            <h2 className="text-3xl font-light">{BOOKING_WIZARD_SLIDES_TITLES[slide]}</h2>
          )}
          <SlideComponent
            setNextDisabled={setIsNextDisabled}
            setNextText={setNextText}
            setSlide={setSlide}
          />
        </YStack>

        {slide !== BOOKING_WIZARD_SLIDES.SUCCESS && (
          <ShadowBox className="fixed bottom-0 z-10 bg-background w-full max-w-160 -ml-6 p-6">
            <XStack className="mt-auto gap-3">
              {previousSlide ? (
                <Button
                  className="flex-1"
                  variant="secondary"
                  disabled={createBookingMutator.isPending}
                  onClick={() => handleSlideChange(previousSlide)}
                >
                  Назад
                </Button>
              ) : null}

              {nextSlide ? (
                <Button
                  className="flex-1"
                  disabled={isNextDisabled}
                  loading={isResolvingNextSlide || createBookingMutator.isPending}
                  onClick={handleNextClick}
                >
                  {isConfirmSlide ? "Забронировать" : nextText}
                </Button>
              ) : null}
            </XStack>
          </ShadowBox>
        )}
      </YStack>
    </BottomSheet>
  );
};
